import { FunctionComponent } from "react";

import { CompanyFindPersonStatus } from "@signalist/shared-types";

import { CompanyPersonFound, CompanyRowInfo } from "@/pages/Private/redux/list/list-companies.api";

interface CompanyPeopleCellProps {
	row: CompanyRowInfo;
	/** Opens the Find person dialog for this one company (its placeholder row). */
	onFindPerson: (row: CompanyRowInfo) => void;
	/** Navigates to the New tab, where the people found for this company are leads. */
	onViewPeople: (row: CompanyRowInfo) => void;
}

/** First letter of a person's name, nullable-safe (no `firstName[0]`, per arch rule user-name). */
const initial = (person: CompanyPersonFound): string => {
	const source = person.firstName || person.name || person.lastName || "?";

	return source.charAt(0).toUpperCase();
};

const formatDate = (value?: Date | null): string =>
	value
		? new Date(value).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
			})
		: "";

/**
 * The People column of a Companies-tab row (PREC-4400): one of the states from the confirmed
 * mockup — "No person searched yet" with a Find person link, "Searching…", the people found as
 * avatars with "N people · in New", "No person found … Searched <date>", "Didn't fit the ICP
 * filter", or "Blacklisted". It is the one place a company row says whether anybody was searched.
 */
export const CompanyPeopleCell: FunctionComponent<CompanyPeopleCellProps> = ({
	row,
	onFindPerson,
	onViewPeople,
}) => {
	if (row.blacklisted) {
		return <span className="text-gray-500">Blacklisted</span>;
	}

	if (row.companyFindPersonStatus === CompanyFindPersonStatus.SEARCHING) {
		return <span className="text-gray-500">Searching for people…</span>;
	}

	if (row.companyFindPersonStatus === CompanyFindPersonStatus.PEOPLE_FOUND) {
		return (
			<button
				className="flex items-center gap-2 text-left"
				type="button"
				onClick={() => onViewPeople(row)}
			>
				<span className="flex -space-x-1">
					{row.peopleFound.slice(0, 3).map((person) => (
						<span
							key={person.id}
							className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-blue-action-200 text-[10px] font-semibold text-blue-action"
							title={person.name ?? undefined}
						>
							{initial(person)}
						</span>
					))}
				</span>
				<span className="text-blue-action hover:underline">
					{row.peopleFoundCount} {row.peopleFoundCount === 1 ? "person" : "people"} · in New
				</span>
			</button>
		);
	}

	if (row.companyFindPersonStatus === CompanyFindPersonStatus.NO_PERSON_FOUND) {
		return (
			<div className="flex flex-col">
				<span className="text-gray-700">No person found at the company</span>
				{row.searchedAt ? (
					<span className="text-xs text-gray-400">Searched {formatDate(row.searchedAt)}</span>
				) : null}
			</div>
		);
	}

	if (row.companyFindPersonStatus === CompanyFindPersonStatus.ICP_MISMATCH) {
		return (
			<div className="flex flex-col">
				<span className="text-gray-500">Didn&apos;t fit the ICP filter · not searched</span>
				{row.exclusionReason ? (
					<span className="text-xs text-gray-400">{row.exclusionReason}</span>
				) : null}
			</div>
		);
	}

	// NOT_SEARCHED (default): the one state that offers the action.
	return (
		<div className="flex items-center gap-3">
			<span className="text-gray-700">No person searched yet</span>
			<button
				className="text-blue-action hover:underline"
				type="button"
				onClick={() => onFindPerson(row)}
			>
				Find person
			</button>
		</div>
	);
};
